import {ContentTile, ContentTileEmptyText} from '#/atoms/content-tile';
import {ActionMenuContext} from '#/components/actionmenu/action-menu';
import {LandCommentEditor} from '#/components/land-comment/land-comment-editor';
import {LandCommentTile} from '#/components/land-comment/land-comment-tile';
import {useLandCommentActions} from '#/components/land-comment/use-land-comment-actions';
import {WalletContext} from '#/contexts/wallet-provider';
import {useLandComments} from '#/hooks/query/use-land-comments';
import {ClassName} from '#/utils/utils';
import {ClockIcon, FireIcon, UserGroupIcon} from '@heroicons/react/24/solid';
import {FunctionComponent, useContext, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {InvestorInsightsFilter} from 'shareland-common';

export const FilterButton: FunctionComponent<{
  filter: InvestorInsightsFilter;
  selectedFilter: InvestorInsightsFilter;
  onClick: (filter: InvestorInsightsFilter) => void;
  text: string;
  IconComponent: FunctionComponent<ClassName>;
}> = ({text, IconComponent, filter, selectedFilter, onClick: propsOnClick}) => {
  const selected = filter === selectedFilter;

  const disabledClassName = 'disabled:opacity-100 disabled:pointer-events-all';
  const className = `inline-flex items-center gap-2 text-white uppercase text-xs font-bold break-keep whitespace-nowrap overflow-hidden opacity-70 hover:opacity-100 transition-all duration-200 ease-in-out ${disabledClassName}`;

  const onClick = () => {
    propsOnClick(filter);
  };

  return (
    <button type='button' className={className} onClick={onClick} disabled={selected}>
      <IconComponent className='w-4 h-4' />
      {text}
    </button>
  );
};

export const ActionMenuContentSectionInvestorInsights: FunctionComponent = () => {
  const {t: tBase} = useTranslation('translation');
  const {t} = useTranslation('translation', {
    keyPrefix: 'pages.actionMenu.information.sections.community.filters',
  });

  const {land} = useContext(ActionMenuContext);
  const {
    accountResult: {address: account},
  } = useContext(WalletContext);

  const [selectedFilter, setSelectedFilter] = useState<InvestorInsightsFilter>('newest');

  const {data: comments, isLoading, refetch} = useLandComments({account, land, filter: selectedFilter});
  const landCommentActions = useLandCommentActions();

  const showComments = comments && comments.results.length > 0;

  return (
    <>
      <LandCommentEditor onCreate={landCommentActions.onCreate} onSuccess={refetch} />

      <div className='bg-grayblue sticky top-0 z-20'>
        <ContentTile className=''>
          <div className='flex justify-around items-center gap-3'>
            <FilterButton
              selectedFilter={selectedFilter}
              onClick={setSelectedFilter}
              text={t('newest')}
              IconComponent={ClockIcon}
              filter={'newest'}
            />
            <FilterButton
              selectedFilter={selectedFilter}
              onClick={setSelectedFilter}
              text={t('trending')}
              IconComponent={FireIcon}
              filter={'trending'}
            />
            <FilterButton
              selectedFilter={selectedFilter}
              onClick={setSelectedFilter}
              text={t('mostPopular')}
              IconComponent={UserGroupIcon}
              filter={'mostPopular'}
            />
          </div>
        </ContentTile>
        <div className='absolute -bottom-3 left-0 w-full h-3 flex pointer-events-none'>
          <div className='w-full h-full bg-grayblue' />
        </div>
      </div>

      {showComments ? (
        <>
          {comments.results.map((comment: any) => (
            <LandCommentTile
              key={comment.id}
              comment={comment}
              refetchComments={refetch}
              landCommentActions={landCommentActions}
            />
          ))}
        </>
      ) : (
        !isLoading && (
          <ContentTile className='grow py-10 flex-center'>
            <ContentTileEmptyText>
              {tBase('pages.actionMenu.information.sections.community.empty')}
            </ContentTileEmptyText>
          </ContentTile>
        )
      )}
    </>
  );
};
